import { type ReactNode } from 'react';
import { FeedCard } from '@/components/comunicados/feed-card';
import type { FeedItem } from '@/components/comunicados/types';
import type { Comunicado, ComposerData } from '../types-comunicados';

type Props = {
  data: ComposerData;
  existing: Comunicado | undefined;
  authorLabel: string;
};

const YT_RX =
  /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/i;
const LOOM_RX = /loom\.com\/(?:share|embed)\/([\w-]{12,})/i;

function urlToEmbed(url: string): FeedItem['embeds'][number] | null {
  const yt = YT_RX.exec(url);
  if (yt) return { provider: 'youtube', url, video_id: yt[1] as string };
  const lm = LOOM_RX.exec(url);
  if (lm) return { provider: 'loom', url, video_id: lm[1] as string };
  return null;
}

function strOr(v: string | undefined, fallback: string): string {
  return v ?? fallback;
}

function previewBase(existing?: Comunicado) {
  const now = new Date().toISOString();
  return {
    id: strOr(existing?.id, 'preview'),
    author: strOr(existing?.author, ''),
    published_at: strOr(existing?.published_at, ''),
    created: strOr(existing?.created, now),
    media: existing?.media ?? [],
  };
}

function buildPreviewItem(data: ComposerData, existing?: Comunicado): FeedItem {
  const embeds = data.embed_urls
    .map(urlToEmbed)
    .filter(Boolean) as FeedItem['embeds'];
  return {
    ...previewBase(existing),
    title: data.title,
    body_html: data.body_html,
    embeds,
  };
}

const SHELL =
  'sticky top-base max-h-[calc(100vh-8rem)] overflow-y-auto rounded-lg border border-white/[0.06] bg-surface/40 p-md';
const HINT = 'mb-sm text-[10px] uppercase tracking-wider text-text-tertiary/60';

export function ComposerPreview({
  data,
  existing,
  authorLabel,
}: Props): ReactNode {
  const item = buildPreviewItem(data, existing);
  return (
    <aside className={SHELL}>
      <p className={HINT}>Vista previa</p>
      <FeedCard
        item={item}
        authorLabel={authorLabel}
        timestamp="Subido hace un momento"
      />
    </aside>
  );
}
